import { AttachmentThumb } from '@/components/renderer/AttachmentThumb'
import type { FormAttachment } from '@/api/attachmentsApi'

export interface ProcedureAttachmentEntry {
  submissionId: string
  formName: string
  attachment: FormAttachment
}

/**
 * Always-visible strip of evidence filed on OTHER steps of this procedure.
 *
 * Second attempt at this feature — the first was a paperclip icon tucked into
 * FormActionBar's rightSlot, opened via click. That turned out not to be
 * "convenient": a small icon among several others is easy to never notice, and
 * this app already has an established pattern for exactly this need —
 * AnchoredFieldsPanel surfaces cross-form context passively, no click required.
 * This mirrors it: same placement (top of the main content, above the open
 * form), same "always there" visibility, distinct tint so the two don't blur
 * together. Actual image thumbnails render inline rather than behind a preview
 * popover, since "see the images" was the literal ask.
 *
 * Independent of whether a completed step's row is itself clickable — this
 * never requires opening or selecting the step that owns the file; it's driven
 * entirely by GET /form-submissions/:id/attachments in the background.
 */
export function ProcedureAttachmentsPanel({ entries }: { entries: ProcedureAttachmentEntry[] }) {
  // Unlike AnchoredFieldsPanel, stays invisible rather than showing an empty
  // hint — most forms have no attachment fields at all, and a permanent empty
  // box on every procedure would just be noise.
  if (entries.length === 0) return null

  return (
    <div className='rounded-xl border border-slate-200 bg-slate-50/60 p-3'>
      <p className='mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500'>
        Evidence from other steps
      </p>
      <div className='flex gap-2 overflow-x-auto pb-1'>
        {entries.map(e => (
          <AttachmentThumb
            key={`${e.submissionId}-${e.attachment.fieldId}-${e.attachment.id}`}
            submissionId={e.submissionId}
            fieldId={e.attachment.fieldId}
            attachmentId={e.attachment.id}
            fileName={e.attachment.fileName}
            mimeType={e.attachment.mimeType}
            storageKey={e.attachment.storageKey}
            sizeBytes={e.attachment.sizeBytes}
            caption={e.formName}
          />
        ))}
      </div>
    </div>
  )
}
